/**
 * MonitoringHeader Component
 *
 * Header section with title, search/filter overlay, and actions menu.
 */

import React from 'react';
import {
  InlineStack,
  BlockStack,
  Text,
  Icon,
  Button,
  Popover,
  ActionList,
} from '@shopify/polaris';
import {
  TargetIcon,
  MenuHorizontalIcon,
  RefreshIcon,
  ExportIcon,
  PlusIcon,
} from '@shopify/polaris-icons';

import { SearchFilterOverlay } from '../index';
import {
  useMonitoringSearch,
  useMonitoringActionsMenu,
  useMonitoringAlertFilters,
  useMonitoringProductFilters,
  useMonitoringFilterActions,
} from '../../../contexts/MonitoringCenterContext';

/**
 * Header with title and actions
 */
export function MonitoringHeader() {
  const search = useMonitoringSearch();
  const actionsMenu = useMonitoringActionsMenu();
  const alertFilters = useMonitoringAlertFilters();
  const productFilters = useMonitoringProductFilters();
  const filterActions = useMonitoringFilterActions();

  return (
    <div className={`monitoring-header ${search.isSearchExpanded ? 'monitoring-header--search-expanded' : ''}`}>
      {/* Left side: Icon and Title */}
      <div className="monitoring-header__title">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Icon source={TargetIcon} tone="subdued" />
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd" fontWeight="semibold">Monitoring Center</Text>
              <Text as="p" variant="bodySm" tone="subdued">Track inventory alerts, products, and historical data</Text>
            </BlockStack>
          </InlineStack>
        </InlineStack>
      </div>

      {/* Right side: Search Trigger + Actions Menu */}
      <div className="monitoring-header__actions">
        {/* Search and Filter Overlay */}
        <div className={`search-filter-wrapper ${search.isSearchExpanded ? 'search-filter-wrapper--expanded' : ''}`}>
          <SearchFilterOverlay
            onExpandChange={search.onSearchExpandChange}
            searchQuery={search.searchQuery}
            onSearchChange={search.onSearchChange}
            placeholder="Search products, SKUs..."
            suggestions={search.autocompleteSuggestions}
            // Alert filters
            severityFilter={alertFilters.severityFilter}
            onSeverityChange={alertFilters.onSeverityChange}
            daysMin={alertFilters.daysMin}
            daysMax={alertFilters.daysMax}
            onDaysMinChange={alertFilters.onDaysMinChange}
            onDaysMaxChange={alertFilters.onDaysMaxChange}
            daysRangeError={alertFilters.daysRangeError}
            inventoryMin={alertFilters.inventoryMin}
            inventoryMax={alertFilters.inventoryMax}
            onInventoryMinChange={alertFilters.onInventoryMinChange}
            onInventoryMaxChange={alertFilters.onInventoryMaxChange}
            inventoryRangeError={alertFilters.inventoryRangeError}
            // Product filters
            statusFilter={productFilters.statusFilter}
            onStatusChange={productFilters.onStatusChange}
            productTypeFilter={productFilters.productTypeFilter}
            onProductTypeChange={productFilters.onProductTypeChange}
            productVendorFilter={productFilters.productVendorFilter}
            onProductVendorChange={productFilters.onProductVendorChange}
            productTypeOptions={productFilters.productTypeOptions}
            productVendorOptions={productFilters.productVendorOptions}
            // Clear and counts
            onClearAllFilters={filterActions.onClearAllFilters}
            totalFilterCount={filterActions.totalFilterCount}
          />
        </div>

        {/* Monitor Wizard Button */}
        <Button
          icon={PlusIcon}
          onClick={actionsMenu.onOpenWizard}
        >
          Monitor wizard
        </Button>

        {/* Actions Menu (context menu) */}
        <Popover
          active={actionsMenu.isActionsMenuOpen}
          activator={
            <Button
              icon={MenuHorizontalIcon}
              onClick={actionsMenu.onActionsMenuToggle}
              accessibilityLabel="More actions"
            />
          }
          onClose={actionsMenu.onActionsMenuClose}
          preferredAlignment="right"
        >
          <ActionList
            items={[
              {
                content: 'Refresh data',
                icon: RefreshIcon,
                onAction: () => {
                  actionsMenu.onActionsMenuClose();
                  actionsMenu.onRefresh();
                },
                disabled: actionsMenu.isRefreshing,
              },
              {
                content: 'Export alerts',
                icon: ExportIcon,
                onAction: () => {
                  actionsMenu.onActionsMenuClose();
                  actionsMenu.onExport();
                },
                disabled: actionsMenu.isExporting,
              },
            ]}
          />
        </Popover>
      </div>
    </div>
  );
}
